import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Container, Stack, Typography, Grid, Box } from '@mui/material';

// Material Icons
import EventNoteIcon from '@mui/icons-material/EventNote';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentIcon from '@mui/icons-material/Payment';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AssignmentIcon from '@mui/icons-material/Assignment';

// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import ViewCardComponent from '../../components/ViewCards';
import { getPayableViewData } from '../../store/Slice/Payable/PayableSlice';
import usePayableSelector from '../../store/Slice/Payable/PayableSelector';
import { NOT_AVAILABLE } from '../Global/Constants';
import { fDateNew } from '../../utils/formatTime';
import { fIndianAmount } from '../../utils/formatNumber';

function ViewPayable() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { PayableViewData } = usePayableSelector();

  const payableId = location.state?.rowId?.paybleId;
  const status = location.state?.rowId?.payableStatus || PayableViewData?.payableStatus;

  const getBankListApi = useCallback(() => {
    if (payableId) {
      dispatch(getPayableViewData(payableId));
    }
  }, [payableId, dispatch]);

  useEffect(() => {
    getBankListApi();
  }, [getBankListApi]);

  const fileDetailsFields = useMemo(
    () => [
      {
        icon: <AssignmentIcon className="viewLabelIcon" size={24} />,
        label: 'File No',
        value: PayableViewData?.fileNo || NOT_AVAILABLE
      },
      {
        icon: <PersonIcon className="viewLabelIcon" size={24} />,
        label: 'Borrower Name',
        value: PayableViewData?.borrowerName || NOT_AVAILABLE
      },
      {
        icon: <AccountBalanceIcon className="viewLabelIcon" size={24} />,
        label: 'Bank Name',
        value: PayableViewData?.bankName || NOT_AVAILABLE
      },
      {
        icon: <LocationCityIcon className="viewLabelIcon" size={24} />,
        label: 'Branch Name',
        value: PayableViewData?.branchName || NOT_AVAILABLE
      },
      {
        icon: <AssignmentIcon className="viewLabelIcon" size={24} />,
        label: 'Loan Name',
        value: PayableViewData?.loanName || NOT_AVAILABLE
      },
      {
        icon: <CheckCircleIcon className="viewLabelIcon" size={24} />,
        label: 'MIS Match Status',
        value:
          PayableViewData?.misIsMatch === true
            ? 'Matched'
            : PayableViewData?.misIsMatch === false
            ? 'Not Matched'
            : NOT_AVAILABLE
      },
      {
        icon: <CalendarTodayIcon className="viewLabelIcon" size={24} />,
        label: 'Created On',
        value: PayableViewData?.createdOn ? fDateNew(PayableViewData.createdOn) : NOT_AVAILABLE
      },
      {
        icon: <EventNoteIcon className="viewLabelIcon" size={24} />,
        label: 'Due Date',
        value: PayableViewData?.dueDate ? fDateNew(PayableViewData.dueDate) : NOT_AVAILABLE
      },
      {
        icon: <PaymentIcon className="viewLabelIcon" size={24} />,
        label: 'Paid On',
        value: PayableViewData?.paidOn ? fDateNew(PayableViewData.paidOn) : NOT_AVAILABLE
      }
    ],
    [PayableViewData]
  );

  const invoiceFields = useMemo(
    () => [
      {
        icon: <ReceiptIcon className="viewLabelIcon" size={24} />,
        label: 'Invoice No',
        value: PayableViewData?.invoiceNo || NOT_AVAILABLE
      },
      {
        icon: <CalendarTodayIcon className="viewLabelIcon" size={24} />,
        label: 'Invoice Date',
        value: PayableViewData?.invoiceDate ? fDateNew(PayableViewData.invoiceDate) : NOT_AVAILABLE
      },
      {
        icon: <ReceiptIcon className="viewLabelIcon" size={24} />,
        label: 'Invoice Amount',
        value: PayableViewData?.invoiceAmount
          ? fIndianAmount(PayableViewData.invoiceAmount)
          : NOT_AVAILABLE
      },
      {
        icon: <PriceCheckIcon className="viewLabelIcon" size={24} />,
        label: 'GST Amount',
        value: PayableViewData?.gstAmount ? fIndianAmount(PayableViewData.gstAmount) : NOT_AVAILABLE
      },
      {
        icon: <PriceCheckIcon className="viewLabelIcon" size={24} />,
        label: 'Total Amount',
        value: PayableViewData?.totalAmount
          ? fIndianAmount(PayableViewData.totalAmount)
          : NOT_AVAILABLE
      },
      {
        icon: <PaymentIcon className="viewLabelIcon" size={24} />,
        label: 'Partner Payout',
        value: PayableViewData?.partnerPayout
          ? fIndianAmount(PayableViewData.partnerPayout)
          : NOT_AVAILABLE
      },
      {
        icon: <CheckCircleIcon className="viewLabelIcon" size={24} />,
        label: 'Payable Status',
        value: PayableViewData?.payableStatus || NOT_AVAILABLE
      }
    ],
    [PayableViewData]
  );

  const partnerFields = useMemo(
    () => [
      {
        icon: <GroupIcon className="viewLabelIcon" size={24} />,
        label: 'Partner Name',
        value: PayableViewData?.partnerName || NOT_AVAILABLE
      },
      {
        icon: <EmailIcon className="viewLabelIcon" size={24} />,
        label: 'Partner Email',
        value: PayableViewData?.partnerEmail || NOT_AVAILABLE
      },
      {
        icon: <PhoneIcon className="viewLabelIcon" size={24} />,
        label: 'Partner Mobile',
        value: PayableViewData?.partnerMobile || NOT_AVAILABLE
      }
    ],
    [PayableViewData]
  );

  return (
    <Page title="Partner Management System || View Payable File">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
            sx={{ mb: 3 }}
          >
            <PageTitle info="View File" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Box className="statusBox">
              <Typography variant="body2">Current Status:</Typography>
              <Typography variant="subtitle2" className="disbursedText">
                {status === 'PENDING'
                  ? 'Pending'
                  : status === 'PAID'
                  ? 'Paid'
                  : status || NOT_AVAILABLE}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <ViewCardComponent title="File & Loan Details" fields={fileDetailsFields} />
            <ViewCardComponent title="Invoice & Financial Details" fields={invoiceFields} />
            <ViewCardComponent title="Partner Information" fields={partnerFields} />
          </Grid>
        </Box>
      </Container>
    </Page>
  );
}

export default ViewPayable;
